import type { LoginPasswordOptions } from '@auth0/auth0-acul-react/types';
import { useErrors } from '@auth0/auth0-acul-react/login-password';
import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import Input from 'creative-ui/uikit/Input';
import InputPassword from 'creative-ui/uikit/Input/InputPassword';
import StatusMessage from 'creative-ui/uikit/StatusMessage';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AuthErrorBanner } from '@/components/AuthErrorBanner/AuthErrorBanner';
import Captcha from '@/components/Captcha';
import { useCaptcha } from '@/components/Captcha/useCaptcha';
import { useTrackErrors } from '@/hooks/useTrackErrors';
import 'creative-ui/tel4/components/styles/ConnexionStyle';

import { useLoginPasswordManager } from '../hooks/useLoginPasswordManager';

function LoginPasswordForm() {
  const { t } = useTranslation(['loginPassword', 'common']);

  const { errors } = useErrors();
  const { generalErrorMessages, fieldErrors } = useTrackErrors(
    errors,
    'login/password/error',
  );

  const {
    data,
    resetPasswordLink,
    handleLoginPassword,
    captcha,
    isCaptchaAvailable,
  } = useLoginPasswordManager();

  const { captchaConfig, captchaProps, captchaValue } = useCaptcha(
    captcha || undefined,
  );

  const form = useForm<LoginPasswordOptions>({
    defaultValues: {
      username: data?.username || '',
      password: '',
      captcha: '',
    },
    reValidateMode: 'onBlur',
  });

  const {
    formState: { isSubmitting, errors: formErrors },
  } = form;

  // Get translations
  const emailLabel = t('form.emailLabel');
  const passwordPlaceholder = t('form.passwordPlaceholder');
  const passwordLabel = t('form.passwordLabel');
  const forgotPasswordLinkText = t('form.forgotPasswordLinkText');
  const continueButtonText = t('form.continueButtonText');
  const emailPasswordSDKError = t('form.emailPasswordSDKError');
  const identifierRequiredError = t('errors.identifierRequired');
  const passwordRequiredError = t('errors.passwordRequired');

  // Proper submit handler with form data
  const onSubmit = async (data: LoginPasswordOptions) => {
    await handleLoginPassword({
      username: data.username,
      password: data.password,
      captcha: isCaptchaAvailable && captchaValue ? captchaValue : undefined,
    });
  };

  const handleForgotPassword = () => {
    if (resetPasswordLink) {
      window.location.assign(resetPasswordLink);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <AuthErrorBanner errors={generalErrorMessages} />
      {fieldErrors.password && (
        <StatusMessage className="u-margin-b-l" mode="error">
          {emailPasswordSDKError}
        </StatusMessage>
      )}
      <Controller
        name="username"
        control={form.control}
        rules={{
          required: identifierRequiredError,
        }}
        render={({ field }) => (
          <Input
            {...field}
            className="u-margin-form"
            type="email"
            disabled
            status={
              formErrors.username || formErrors.root ? 'error' : undefined
            }
            statusMessage={
              formErrors.username?.message || formErrors.root?.message
            }>
            {emailLabel}
          </Input>
        )}
      />
      <Controller
        name="password"
        control={form.control}
        rules={{
          required: passwordRequiredError,
        }}
        render={({ field }) => (
          <InputPassword
            {...field}
            className="u-margin-form"
            placeholder={passwordPlaceholder}
            status={formErrors.password ? 'error' : undefined}
            statusMessage={formErrors.password?.message}>
            {passwordLabel}
          </InputPassword>
        )}
      />

      {isCaptchaAvailable && captchaConfig && (
        <Captcha
          captcha={captchaConfig}
          name="captcha"
          control={form.control}
          setError={form.setError}
          clearErrors={form.clearErrors}
          error={fieldErrors.captcha}
          rules={{ required: t('common:captcha.errorRequired') }}
          {...captchaProps}
        />
      )}
      <CtaContainer stack>
        <Cta
          className="c-submitCta"
          type="primary"
          submit
          disabled={isSubmitting}
          isWaiting={isSubmitting}>
          {continueButtonText}
        </Cta>
        <Cta type="ghost" onClick={handleForgotPassword}>
          {forgotPasswordLinkText}
        </Cta>
      </CtaContainer>
    </form>
  );
}

export default LoginPasswordForm;
